import { createRef, useCallback, useEffect, useMemo, useState } from 'react';
import { AgendaApi } from 'services';
import { Box, CircularProgress, Grid, Paper, Typography } from '@material-ui/core';
import { initialEmployeeState } from 'config/constants';
import { useEmployee, useEmployeeFunctions } from 'contexts/EmployeeContext';
import addEmployeeIcon from '_assets/activity/addEmployee.svg';
import addEmployeeHoveredIcon from '_assets/activity/addEmployeeHovered.svg';
import DefaultIconButton from 'components/DefaultIconButton';
import DefaultModal from '../DefaultModal';
import EmployeeForm from 'components/EmployeeForm';
import env from 'config/env';
import FullCalendar from '@fullcalendar/react';
import frLocale from '@fullcalendar/core/locales/fr';
import interactionPlugin from '@fullcalendar/interaction';
import moment from 'moment';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import useStyles from './styles';

const dayNumbers = {
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
  Sunday: 7,
};

const renderResourceHeaderContent = (classes) => {
  return (
    <Box className={classes.resourceHeader}>
      <Typography style={{ fontSize: 21 }}>Moniteurs</Typography>
    </Box>
  );
};

const renderResourceLabel = ({ fieldValue }) => {
  return (
    <Typography component="p" variant="h5">
      {fieldValue}
    </Typography>
  );
};

const renderEventContent = (eventInfo, classes) => {
  return (
    <div style={{ display: 'flex', alignContent: 'center', alignItems: 'center' }}>
      <Typography className={classes.slotText}>
        <span style={{ fontSize: 10 }}>({eventInfo.timeText})</span>
      </Typography>

      <Typography className={classes.slotText}>
        <span style={{ marginLeft: 8, fontWeight: 500 }}>{eventInfo.event.title}</span>
      </Typography>
    </div>
  );
};

function AgendaTransferModal({ employee, open, onConfirm, title, ...rest }) {
  const classes = useStyles();
  const { employees } = useEmployee();
  const { createEmployee } = useEmployeeFunctions();

  const resources = useMemo(() => {
    const enabledEmployees = employees.filter((e) => e.is_enabled);
    return enabledEmployees.map((e) => ({
      ...e,
      title: `${e.first_name} ${e.last_name}`,
      selected: e.id === employee?.id,
    }));
  }, [employee?.id, employees]);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [createEmployeeModalOpen, setCreateEmployeeModalOpen] = useState(false);

  let calendarReference = createRef();

  const fetchResourcesAgenda = useCallback(async () => {
    if (!employee.id) return;

    setLoading(true);

    let events = [];
    for (const res of resources) {
      const result = await AgendaApi.getWeekAgendaByEmployee(res.id);

      for (const activity of result.agenda.activities) {
        const dayNumber = dayNumbers[activity.week_day];
        let startDate = moment().day(dayNumber).startOf('day');
        const [startHour, startMin] = activity.start_time.split(':');
        startDate.set({ hour: Number(startHour), minute: Number(startMin) });

        let endDate = moment().day(dayNumber).startOf('day');
        const [endHour, endMin] = activity.end_time.split(':');
        endDate.set({ hour: Number(endHour), minute: Number(endMin) });

        events.push({
          id: activity.id,
          conflict: res.id === employee.id ? true : null,
          day_number: dayNumber,
          end: endDate.format(),
          resourceId: res.id,
          service_id: activity.service_id,
          start: startDate.format(),
          title: activity.service_name,
        });
      }
    }
    setEvents(events);
    setLoading(false);
  }, [employee?.id, resources]);

  useEffect(() => {
    if (open) setTimeout(fetchResourcesAgenda);
  }, [fetchResourcesAgenda, open]);

  const checkEvent = (dropLocation, draggedEvent) => {
    return Number(dropLocation.resource.id) !== employee.id && draggedEvent.extendedProps.conflict !== null;
  };

  const getEventClassNames = ({ event }) => {
    const conflict = event.extendedProps.conflict;
    const eventClasses =
      conflict !== null ? (conflict === true ? classes.conflictEvent : classes.resolvedEvent) : undefined;
    return [classes.event, eventClasses];
  };

  const handleChange = (eventInfo) => {
    const { oldEvent, event } = eventInfo;
    if (!oldEvent.id) return;

    let newStartDate = moment(event.start);
    let newEndDate = moment(event.end);
    const resourceId = event._def.resourceIds[0];

    // Check hours with other events
    const conflictDate = events.find((e) => {
      const start = moment(e.start);
      const end = moment(e.end);
      return (
        e.resourceId === Number(resourceId) &&
        e.id !== Number(event.id) &&
        newStartDate.isBetween(start, end, null, '[)')
      );
    });

    let newEvent = {
      id: Number(event.id),
      conflict: conflictDate !== undefined,
      day_number: newStartDate.day(),
      end: newEndDate.format(),
      resourceId: Number(event._def.resourceIds[0]),
      service_id: event.extendedProps.service_id,
      start: newStartDate.format(),
      title: event.title,
    };

    const index = events.findIndex((e) => e.id === Number(event.id));
    let newEvents = [...events];
    newEvents.splice(index, 1, newEvent);
    setEvents(newEvents);
  };

  const onCreateEmployeeClick = () => {
    setCreateEmployeeModalOpen(true);
  };

  const handleCreateEmployee = async ({ values, picture }) => {
    let pictureData = null;
    if (picture?.file) {
      let formData = new FormData();
      formData.append('file', picture?.file);
      pictureData = { data: formData, url: picture?.url };
    }

    const { created, pictureUpdated } = await createEmployee(values, pictureData);
    if (created) setCreateEmployeeModalOpen(false);

    return { pictureUpdated, success: created };
  };

  const handleTransfer = () => {
    if (events.some((e) => e.conflict)) {
      setError(true);
      return;
    }

    onConfirm?.(events.filter((e) => e.conflict === false));
  };

  return (
    <DefaultModal maxWidth="lg" open={open} onConfirm={handleTransfer} {...rest}>
      {loading ? (
        <Box className={classes.circleContainer}>
          <CircularProgress size={40} />
        </Box>
      ) : (
        <Paper className={classes.agendaContainer}>
          <Grid container className={classes.agendaHeader}>
            <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center', marginBottom: '-5%' }}>
              <Typography className={classes.title}>{title}</Typography>
            </Grid>

            <Grid item>
              <DefaultIconButton
                color="primary"
                variant="contained"
                onClick={onCreateEmployeeClick}
                icon={addEmployeeIcon}
                hoveredIcon={addEmployeeHoveredIcon}
                iconAlt="Icone Ajout d'employee"
                iconText="Moniteur"
              />
            </Grid>
          </Grid>

          <FullCalendar
            ref={calendarReference}
            schedulerLicenseKey={env.fullCalendarLicenseKey}
            droppable={true}
            editable={true}
            eventAllow={checkEvent}
            eventChange={handleChange}
            eventClassNames={(event) => getEventClassNames(event)}
            events={events}
            eventContent={(eventInfo) => renderEventContent(eventInfo, classes)}
            headerToolbar={false}
            height="auto"
            initialView="resourceTimelineWeek"
            locale={frLocale}
            nowIndicator={false}
            plugins={[interactionPlugin, resourceTimelinePlugin]}
            resourceAreaHeaderContent={() => renderResourceHeaderContent(classes)}
            resourceAreaWidth={200}
            resourceLabelClassNames={classes.resourceLabelContainer}
            resourceLabelContent={renderResourceLabel}
            resourceOrder="-selected"
            resources={resources}
            selectMirror={true}
            slotLabelFormat={[{ weekday: 'long' }]}
            slotMaxTime="23:00"
            slotMinTime="05:00"
            viewClassNames={classes.view}
            views={{
              resourceTimelineWeek: {
                type: 'resourceTimelineWeek',
                duration: { weeks: 1 },
                slotDuration: { days: 1 },
                slotLabelInterval: { days: 1 },
              },
            }}
          />

          <DefaultModal hideActions maxWidth="md" open={createEmployeeModalOpen} title="Création d'un moniteur">
            <EmployeeForm
              id="employeeForm2"
              dialog
              employee={initialEmployeeState}
              onCancel={() => setCreateEmployeeModalOpen(false)}
              onSubmit={handleCreateEmployee}
            />
          </DefaultModal>

          <DefaultModal forceConfirm info title="Attention" square open={error} onClose={() => setError(false)}>
            <Box className={classes.modalContent}>
              <Typography className={classes.modalText} align="center">
                Impossible de transférer l'agenda, veuillez d'abord résoudre les conflits
              </Typography>
            </Box>
          </DefaultModal>
        </Paper>
      )}
    </DefaultModal>
  );
}

export default AgendaTransferModal;
